import React, { useState, useMemo, useCallback } from 'react';

/**
 * Generic bar chart with support for multiple series (grouped bars per x-point).
 * X-axis (zero line) is at the bottom by default for positive-only data.
 * When negative values exist, the chart adapts proportionally.
 * Reusable for any data: game stats, currency stats, etc.
 */

export interface ChartDataPoint<X = number | string> {
  /** X-axis value (timestamp, category id, etc.) */
  x: X;
  /** Series name -> value. Each series is a separate bar group at this x position. */
  series: Record<string, number>;
}

export interface BarChartProps<X = number | string> {
  /** Chart data: array of x-points with series values */
  data: ChartDataPoint<X>[];
  /** Format x value for axis labels */
  formatXLabel: (x: X) => string;
  /** Format x value for tooltip (optional, falls back to formatXLabel) */
  formatXLabelFull?: (x: X) => string;
  /** Format y value for axis and tooltip */
  formatYValue: (value: number) => string;
  /** Optional: custom tooltip content renderer */
  formatTooltip?: (x: X, seriesName: string, value: number) => string;
  /** Optional: custom labels for legend (default: series name) */
  seriesLabels?: Record<string, string>;
  /** Optional: suffix for legend items (e.g. " GGR") */
  legendSuffix?: string;
  /** Chart height */
  height?: number;
  /** Minimum width per bar group (affects total chart width) */
  minBarGroupWidth?: number;
  /** Bar colors for series (cycled if more series than colors) */
  colors?: string[];
  /** Whether to show legend with series selection */
  showLegend?: boolean;
}

const DEFAULT_COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4'];

export function BarChart<X = number | string>({
  data,
  formatXLabel,
  formatXLabelFull,
  formatYValue,
  formatTooltip,
  seriesLabels = {},
  legendSuffix = '',
  height = 400,
  minBarGroupWidth = 50,
  colors = DEFAULT_COLORS,
  showLegend = true,
}: BarChartProps<X>) {
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ clientX: number; clientY: number } | null>(null);

  // Ensure every data point gets enough space
  const width = Math.max(800, data.length * minBarGroupWidth);
  const padding = { top: 40, right: 40, bottom: 60, left: 80 };

  const seriesList = useMemo(() => {
    const set = new Set<string>();
    data.forEach(point => Object.keys(point.series).forEach(k => set.add(k)));
    return Array.from(set);
  }, [data]);

  const allValues = useMemo(() => {
    const vals: number[] = [];
    data.forEach(point => {
      seriesList.forEach(s => vals.push(point.series[s] ?? 0));
    });
    return vals;
  }, [data, seriesList]);

  // Compute Y range: yMax >= 0, yMin <= 0 (always include zero)
  const { yMax, yMin, totalRange } = useMemo(() => {
    const max = Math.max(...allValues, 0);
    const min = Math.min(...allValues, 0);
    const range = (max - min) || 1;
    return { yMax: max || 1, yMin: min, totalRange: range };
  }, [allValues]);

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const slotWidth = data.length > 0 ? chartWidth / data.length : 0;

  // Zero line position: at bottom when all positive, moves up proportionally when negatives exist
  const zeroY = padding.top + (yMax / totalRange) * chartHeight;
  const bottomY = padding.top + chartHeight;

  const xCenter = useCallback(
    (index: number) => padding.left + (index + 0.5) * slotWidth,
    [padding.left, slotWidth]
  );

  const yScale = useCallback(
    (value: number) => padding.top + ((yMax - value) / totalRange) * chartHeight,
    [padding.top, yMax, totalRange, chartHeight]
  );

  // Bar sizing: use a good portion of the slot
  const barGroupWidth = slotWidth * 0.7;
  const barWidth = seriesList.length > 0
    ? Math.max(barGroupWidth / seriesList.length * 0.8, 2)
    : 0;

  const formatXFull = formatXLabelFull ?? formatXLabel;

  // Build tooltip content for hovered slot (shows ALL series values)
  const tooltipContent = useMemo(() => {
    if (hoveredIndex === null || hoveredIndex < 0 || hoveredIndex >= data.length) return null;
    const point = data[hoveredIndex];
    return {
      x: point.x,
      entries: seriesList.map(s => ({
        name: seriesLabels[s] ?? s,
        value: point.series[s] ?? 0,
        color: colors[seriesList.indexOf(s) % colors.length],
      })),
    };
  }, [hoveredIndex, data, seriesList, seriesLabels, colors]);

  // X-axis labels: show every label, rotate if needed
  const xLabelStep = data.length > 31 ? Math.ceil(data.length / 20) : 1;

  return (
    <div style={{ overflowX: 'auto', position: 'relative' }}>
      {/* Tooltip */}
      {tooltipContent && mousePos && (
        <div
          style={{
            position: 'fixed',
            left: mousePos.clientX + 12,
            top: mousePos.clientY - 12,
            padding: '10px 14px',
            backgroundColor: 'rgba(0,0,0,0.88)',
            color: 'white',
            borderRadius: '8px',
            fontSize: '13px',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            maxWidth: '350px',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '4px' }}>
            {formatXFull(tooltipContent.x)}
          </div>
          {tooltipContent.entries.map((entry, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: entry.color, flexShrink: 0 }} />
              <span>{entry.name}: <strong>{formatYValue(entry.value)}</strong></span>
            </div>
          ))}
        </div>
      )}

      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Positive grid lines */}
        {yMax > 0 && [0.25, 0.5, 0.75, 1].map(ratio => {
          const val = yMax * ratio;
          const y = yScale(val);
          return (
            <g key={`pos-${ratio}`}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#666">{formatYValue(val)}</text>
            </g>
          );
        })}
        {/* Negative grid lines (only when negative values exist) */}
        {yMin < 0 && [0.25, 0.5, 0.75, 1].map(ratio => {
          const val = yMin * ratio;
          const y = yScale(val);
          return (
            <g key={`neg-${ratio}`}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#666">{formatYValue(val)}</text>
            </g>
          );
        })}
        {/* Zero line label */}
        <text x={padding.left - 10} y={zeroY + 4} textAnchor="end" fontSize="12" fill="#333" fontWeight="bold">{formatYValue(0)}</text>

        {/* Y-axis */}
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={bottomY} stroke="#333" strokeWidth="2" />
        {/* Zero line (X-axis baseline for bars) */}
        <line x1={padding.left} y1={zeroY} x2={width - padding.right} y2={zeroY} stroke="#333" strokeWidth="2" />
        {/* Bottom border if zero is not at bottom */}
        {yMin < 0 && (
          <line x1={padding.left} y1={bottomY} x2={width - padding.right} y2={bottomY} stroke="#ccc" strokeWidth="1" />
        )}

        {/* X-axis ticks and labels — always at bottom */}
        {data.map((point, i) => {
          const cx = xCenter(i);
          const slotStart = padding.left + i * slotWidth;
          const showLabel = i % xLabelStep === 0;
          return (
            <g key={`x-${i}`}>
              {/* Tick at bottom */}
              <line x1={slotStart} y1={bottomY - 4} x2={slotStart} y2={bottomY + 4} stroke="#666" strokeWidth="1" />
              {/* Label at bottom */}
              {showLabel && (
                <text x={cx} y={bottomY + 18} textAnchor="middle" fontSize="11" fill="#444">
                  {formatXLabel(point.x)}
                </text>
              )}
            </g>
          );
        })}
        {/* Last right tick */}
        {data.length > 0 && (
          <line x1={padding.left + data.length * slotWidth} y1={bottomY - 4} x2={padding.left + data.length * slotWidth} y2={bottomY + 4} stroke="#666" strokeWidth="1" />
        )}

        {/* Hover highlight column */}
        {hoveredIndex !== null && (
          <rect
            x={padding.left + hoveredIndex * slotWidth}
            y={padding.top}
            width={slotWidth}
            height={chartHeight}
            fill="rgba(0,0,0,0.04)"
            pointerEvents="none"
          />
        )}

        {/* Bars */}
        {seriesList.map((seriesName, seriesIndex) => {
          const color = colors[seriesIndex % colors.length];
          const isSelected = selectedSeries === null || selectedSeries === seriesName;
          const opacity = isSelected ? 0.8 : 0.15;

          return (
            <g key={seriesName}>
              {data.map((point, pointIndex) => {
                const value = point.series[seriesName] ?? 0;
                const cx = xCenter(pointIndex);
                const barX = cx - barGroupWidth / 2 + seriesIndex * (barGroupWidth / seriesList.length) + (barGroupWidth / seriesList.length - barWidth) / 2;
                const rawH = Math.abs(yScale(value) - zeroY);
                const barH = Math.max(rawH, 2);
                const barY = value >= 0 ? zeroY - barH : zeroY;

                return (
                  <rect
                    key={pointIndex}
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barH}
                    fill={color}
                    opacity={opacity}
                    rx="1"
                  />
                );
              })}
            </g>
          );
        })}

        {/* Invisible hit areas — full slot width, full chart height, for reliable hover */}
        {data.map((_point, i) => (
          <rect
            key={`hit-${i}`}
            x={padding.left + i * slotWidth}
            y={padding.top}
            width={slotWidth}
            height={chartHeight}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => {
              setHoveredIndex(i);
              setMousePos({ clientX: e.clientX, clientY: e.clientY });
            }}
            onMouseMove={(e) => {
              setMousePos({ clientX: e.clientX, clientY: e.clientY });
            }}
            onMouseLeave={() => {
              setHoveredIndex(null);
              setMousePos(null);
            }}
          />
        ))}
      </svg>

      {/* Legend */}
      {showLegend && seriesList.length > 0 && (
        <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {seriesList.map((seriesName, index) => {
            const color = colors[index % colors.length];
            const isSelected = selectedSeries === null || selectedSeries === seriesName;
            return (
              <div
                key={seriesName}
                onClick={() =>
                  setSelectedSeries(isSelected && selectedSeries === seriesName ? null : seriesName)
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  opacity: isSelected ? 1 : 0.4,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: isSelected && selectedSeries === seriesName ? '#f0f0f0' : 'transparent',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ width: 14, height: 14, backgroundColor: color, borderRadius: 2 }} />
                <span style={{ fontSize: '13px', color: '#333', fontWeight: isSelected && selectedSeries === seriesName ? 600 : 400 }}>
                  {seriesLabels[seriesName] ?? seriesName}{legendSuffix}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
